import { CompilerOptions, TRANSLATIONS, TRANSLATIONS_FORMAT } from '@angular/core';
import { BootstrapOptions } from '@angular/core/src/application_ref';

const xliffMergeConfig = require('../../../../xliffmerge.json');
const config = require('assets/config.json');

function getCookie(document: Partial<Document>, sKey: string): string {
	return decodeURIComponent(document.cookie.replace(new RegExp('(?:(?:^|.*;)\\s*' + encodeURIComponent(sKey).replace(/[\-\.\+\*]/g, '\\$&') + '\\s*\\=\\s*([^;]*).*$)|^.*$'), '$1')) || null;
}

function setCookie(document: Partial<Document>, sKey: string, sValue: string, vEnd?: number|string|Date|false, sPath?: string, sDomain?: string, bSecure?: boolean): boolean {
	if (!sKey || /^(?:expires|max\-age|path|domain|secure)$/i.test(sKey)) {
		return false;
	}

	let sExpires = '';

	if (vEnd) {
		switch (vEnd.constructor) {
			case Number:
				sExpires = vEnd === Infinity ? '; expires=Fri, 31 Dec 9999 23:59:59 GMT' : '; max-age=' + vEnd;
				break;

			case String:
				sExpires = '; expires=' + vEnd;
				break;

			case Date:
				sExpires = '; expires=' + (<Date>vEnd).toUTCString();
				break;
		}
	}

	document.cookie = `${encodeURIComponent(sKey)}=${encodeURIComponent(sValue)}${sExpires}${sDomain ? '; domain=' + sDomain : ''}${sPath ? '; path=' + sPath : ''}${bSecure ? '; secure' : ''}`;

	return true;
}

/**
 * Returns the language code name from the browser, e.g. "de"
 * copied from https://github.com/ngx-translate/core/blob/master/src/translate.service.ts
 *
 * @returns string
 */
export function getBrowserLang(window: Partial<Window>): string {
	if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
		return undefined;
	}

	let browserLang: any = window.navigator['languages'] ? window.navigator['languages'][0] : null;
	browserLang = browserLang || window.navigator.language || window.navigator['browserLanguage'] || window.navigator['userLanguage'];

	if (browserLang.indexOf('-') !== -1) {
		browserLang = browserLang.split('-')[0];
	}

	if (browserLang.indexOf('_') !== -1) {
		browserLang = browserLang.split('_')[0];
	}

	return browserLang;
}

/**
 * Returns the culture language code name from the browser, e.g. "de-DE"
 * copied from https://github.com/ngx-translate/core/blob/master/src/translate.service.ts
 *
 * @returns string
 */
export function getBrowserCultureLang(window: Partial<Window>): string {
	if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
		return undefined;
	}

	let browserCultureLang: any = window.navigator['languages'] ? window.navigator['languages'][0] : null;
	browserCultureLang = browserCultureLang || window.navigator.language || window.navigator['browserLanguage'] || window.navigator['userLanguage'];

	return browserCultureLang;
}

/**
 * Returns the current lang for the application
 * using the existing base path
 * or the browser lang if there is no base path
 * @returns {string}
 */
export function getLang(window: Partial<Window>): string {
    let lang = getCookie(window.document, 'lang') || window.localStorage.getItem('lang') || config.i18n.default;
	const langMatchRegex = new RegExp("^(" + xliffMergeConfig.xliffmergeOptions.languages.join('|') + ")$");
	lang = lang.match(langMatchRegex) ? lang : config.i18n.default;
	setLang(window, lang);
	return lang;
}

export function setLang(window: Partial<Window>, language: string) {
	window.localStorage.setItem('lang', language);
	setCookie(window.document, 'lang', language, false, '/');
}

export function detectAndSetLanguage(window: Partial<Window>): void {
	if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
		return null;
	}

	let lang = getCookie(window.document, 'lang') || window.localStorage.getItem('lang') || '';

	if (!lang) {
		lang = getBrowserLang(window);
	}

	const langMatchRegex = new RegExp(`^(${xliffMergeConfig.xliffmergeOptions.languages.join('|')})$`);
	lang = lang.match(langMatchRegex) ? lang : config.i18n.default;
	setLang(window, lang);
}

/**
 * Lazy-load the translations in JIT
 * or does nothing in AOT
 * @param isAot a parameter to know if the app is using AOT or not
 * @returns {any}
 */
export function getTranslationProviders(isAot: boolean): Promise<(CompilerOptions & BootstrapOptions) | Array<CompilerOptions & BootstrapOptions>> {
	if (isAot) {
		return Promise.resolve([]);
	}

	detectAndSetLanguage(window);

	const locale = getLang(window);

	// No locale or default lang
	if (!locale || locale === config.i18n.default) {
		return Promise.resolve([]);
	}

	return getTranslationsWithSystemJs(locale)
		.then((translations: string) => <(CompilerOptions & BootstrapOptions) | Array<CompilerOptions & BootstrapOptions>>[
			{provide: TRANSLATIONS, useValue: translations},
			{provide: TRANSLATIONS_FORMAT, useValue: 'xlf'},
		])
		.catch((e) => []); // ignore if file not found
}

function getTranslationsWithSystemJs(locale: string) {
	return System.import(`raw-loader!../../../i18n/messages.${locale}.xlf`); // relies on text plugin
}



// WEBPACK FOOTER //
// ./src/app/shared/i18n/i18n-providers.ts